import { SlinguAvatar, type SlinguMood, Logo, useSfx } from '@slingu/ui';
import { useEffect, useRef, useState } from 'react';

import './landing.css';

export type LandingProps = {
  /** Triggered when the user taps "Iniciar". Login/register is disabled
   * for now — this takes them straight into the app as a guest. */
  onLogin?: () => void;
  onRegister: () => void;
};

const MOOD_SEQUENCE: Array<{ mood: SlinguMood; holdMs: number }> = [
  { mood: 'idle', holdMs: 3200 },
  { mood: 'happy', holdMs: 1400 },
  { mood: 'idle', holdMs: 2800 },
  { mood: 'wave', holdMs: 1600 },
  { mood: 'idle', holdMs: 3600 },
  { mood: 'focus', holdMs: 1200 },
  { mood: 'idle', holdMs: 2400 },
  { mood: 'happy', holdMs: 1800 },
];

export function Landing({ onRegister }: LandingProps) {
  const [moodIdx, setMoodIdx] = useState(0);
  const [launching, setLaunching] = useState(false);
  const [launchMood, setLaunchMood] = useState<SlinguMood>('wave');
  const sfx = useSfx();
  const launchedRef = useRef(false);

  useEffect(() => {
    if (launching) return;
    let cancelled = false;
    let timerId: number | null = null;
    const step = (idx: number) => {
      if (cancelled) return;
      setMoodIdx(idx);
      const hold = MOOD_SEQUENCE[idx]?.holdMs ?? 2400;
      timerId = window.setTimeout(() => {
        step((idx + 1) % MOOD_SEQUENCE.length);
      }, hold);
    };
    step(0);
    return () => {
      cancelled = true;
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [launching]);

  useEffect(() => {
    if (!launching) {
      setLaunchMood('wave');
      return;
    }
    setLaunchMood('wave');
    const peak = window.setTimeout(() => setLaunchMood('happy'), 400);
    const land = window.setTimeout(() => setLaunchMood('wave'), 700);
    return () => {
      window.clearTimeout(peak);
      window.clearTimeout(land);
    };
  }, [launching]);

  const start = () => {
    if (launchedRef.current) return;
    launchedRef.current = true;
    setLaunching(true);
    sfx.play('start');
    window.setTimeout(() => sfx.play('jump'), 140);
    window.setTimeout(() => {
      onRegister();
    }, 920);
  };

  return (
    <div className="sl-landing sl-landing--intro">
      <header className="sl-landing__topbar">
        <div className="sl-landing__brand">
          <Logo size={28} />
          <span>Slingu</span>
        </div>
      </header>

      <main className="sl-intro">
        <div className={`sl-intro__stage${launching ? ' is-launching' : ''}`}>
          <div className="sl-intro__avatar">
            <SlinguAvatar
              size={220}
              mood={launching ? launchMood : (MOOD_SEQUENCE[moodIdx]?.mood ?? 'idle')}
            />
          </div>

          <div className="sl-intro__shadow" aria-hidden />

          {launching ? (
            <>
              <div className="sl-intro__dust" aria-hidden>
                <span /><span /><span /><span /><span />
              </div>
              <div className="sl-intro__sparkles" aria-hidden>
                <span /><span /><span /><span /><span /><span />
              </div>
            </>
          ) : null}
        </div>

        <button
          type="button"
          className="sl-intro__cta"
          onClick={start}
          disabled={launching}
          aria-label="Iniciar"
        >
          <span className="sl-intro__cta-shine" aria-hidden />
          <span className="sl-intro__cta-label">
            {launching ? 'Vamos!' : 'Iniciar'}
          </span>
        </button>
      </main>

      <footer className="sl-landing__footer">
        <span>© 2026 Slingu · Languages, gamified.</span>
      </footer>
    </div>
  );
}
